import { useMemo } from 'react'
import { Button } from '../../../components/ui/button'
import { Card } from '../../../components/ui/card'
import type { MonthlyRowEntry } from './interfaces/MonthlyRecord'

const SYNC_FIELDS = [
  'elec_unit_price',
  'elec_prev_reading',
  'elec_curr_reading',
  'water_unit_price',
  'water_prev_reading',
  'water_curr_reading',
] as const

const FIELD_LABELS: Record<(typeof SYNC_FIELDS)[number], string> = {
  elec_unit_price: 'Elec ฿/Unit',
  elec_prev_reading: 'Elec Prev',
  elec_curr_reading: 'Elec Curr',
  water_unit_price: 'Water ฿/Unit',
  water_prev_reading: 'Water Prev',
  water_curr_reading: 'Water Curr',
}

interface SyncUtilitiesPreviewModalProps {
  rows: MonthlyRowEntry[]
  onConfirm: (syncRows: MonthlyRowEntry[]) => void
  onCancel: () => void
  isPending: boolean
}

export function SyncUtilitiesPreviewModal({
  rows,
  onConfirm,
  onCancel,
  isPending,
}: SyncUtilitiesPreviewModalProps) {
  const syncRows = useMemo(() => {
    return rows.filter((row) => {
      if (row.source !== 'snapshot') return false

      // If there's no existing record, we will create one, so it's a diff
      if (!row.existingRecord) return true

      // If there is an existing record, check if any of the sync fields differ
      for (const field of SYNC_FIELDS) {
        const snapVal = row.snapshotForm?.[field]
        const existVal = String(row.existingRecord.util[field])
        if (snapVal !== existVal) {
          return true
        }
      }
      return false
    })
  }, [rows])

  const newRows = syncRows.filter((row) => !row.existingRecord)
  const editedRows = syncRows.filter((row) => !!row.existingRecord)

  const getBeforeValue = (
    entry: MonthlyRowEntry,
    field: (typeof SYNC_FIELDS)[number],
  ) => {
    if (!entry.existingRecord) return null
    return String(entry.existingRecord.util[field])
  }

  const getAfterValue = (
    entry: MonthlyRowEntry,
    field: (typeof SYNC_FIELDS)[number],
  ) => {
    return entry.snapshotForm?.[field] || ''
  }

  const renderRows = (entries: MonthlyRowEntry[], isNew: boolean) => {
    return entries.map((entry) => {
      const tenantName =
        entry.tenantName ||
        (entry.contract?.tenant
          ? `${entry.contract.tenant.first_name ?? ''} ${entry.contract.tenant.last_name ?? ''}`.trim()
          : '—')

      return (
        <tr key={entry.room.room_number} className="hover:bg-gray-50">
          <td className="whitespace-nowrap px-3 py-3 font-medium">
            {entry.room.room_number}
          </td>
          <td className="whitespace-nowrap px-3 py-3 text-gray-600">
            {tenantName || '—'}
          </td>
          {SYNC_FIELDS.map((field) => {
            const before = getBeforeValue(entry, field)
            const after = getAfterValue(entry, field)
            const fieldChanged = before !== after
            const isMissingInLive = isNew && before === null

            return (
              <td key={field} className="px-3 py-3">
                {isMissingInLive ? (
                  <div className="flex items-center gap-1.5">
                    <span className="inline-flex rounded-full bg-blue-100 px-2 py-0.5 font-semibold text-blue-700 text-xs">
                      New
                    </span>
                    <span className="text-gray-700">{after || '—'}</span>
                  </div>
                ) : fieldChanged ? (
                  <div className="flex flex-col gap-0.5">
                    <span className="text-gray-400 text-xs line-through">
                      {before || '—'}
                    </span>
                    <span className="font-medium text-gray-900">
                      {after || '—'}
                    </span>
                  </div>
                ) : (
                  <span className="text-gray-300">{before || '—'}</span>
                )}
              </td>
            )
          })}
        </tr>
      )
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <Card className="flex w-full max-w-4xl flex-col gap-4 p-6">
        <div>
          <h3 className="font-semibold text-gray-900 text-lg">
            Preview Utilities Sync
          </h3>
          <p className="text-gray-500 text-sm">
            {syncRows.length} room{syncRows.length !== 1 ? 's' : ''} have
            updated water or electricity readings in this snapshot.
          </p>
          <p className="text-red-500 text-sm">
            After you confirm, this modal will update or create records in the
            utilities table.
          </p>
        </div>

        <div className="max-h-[60vh] overflow-y-auto rounded-md border border-gray-200">
          <table className="min-w-full text-sm">
            <thead className="sticky top-0 z-10 bg-gray-50">
              <tr className="border-gray-200 border-b">
                <th className="whitespace-nowrap px-3 py-2 text-left font-medium text-gray-500">
                  Room
                </th>
                <th className="whitespace-nowrap px-3 py-2 text-left font-medium text-gray-500">
                  Tenant
                </th>
                {SYNC_FIELDS.map((f) => (
                  <th
                    key={f}
                    className="whitespace-nowrap px-3 py-2 text-left font-medium text-gray-500"
                  >
                    {FIELD_LABELS[f]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {syncRows.length === 0 && (
                <tr>
                  <td
                    colSpan={SYNC_FIELDS.length + 2}
                    className="px-3 py-8 text-center text-gray-500"
                  >
                    No utility updates found between snapshot and live records.
                  </td>
                </tr>
              )}
              {newRows.length > 0 && (
                <>
                  <tr className="bg-blue-50">
                    <td
                      colSpan={SYNC_FIELDS.length + 2}
                      className="px-3 py-2 font-semibold text-blue-900 text-xs uppercase tracking-wide"
                    >
                      🔵 New Records ({newRows.length})
                    </td>
                  </tr>
                  {renderRows(newRows, true)}
                </>
              )}
              {editedRows.length > 0 && (
                <>
                  <tr className="bg-amber-50">
                    <td
                      colSpan={SYNC_FIELDS.length + 2}
                      className="px-3 py-2 font-semibold text-amber-900 text-xs uppercase tracking-wide"
                    >
                      🟠 Edited Records ({editedRows.length})
                    </td>
                  </tr>
                  {renderRows(editedRows, false)}
                </>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex justify-end gap-2 border-gray-100 border-t pt-2">
          <Button variant="outline" onClick={onCancel} disabled={isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => onConfirm(syncRows)}
            disabled={isPending || syncRows.length === 0}
          >
            {isPending ? 'Syncing…' : 'Confirm & Sync'}
          </Button>
        </div>
      </Card>
    </div>
  )
}
